import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuth'
import { api } from '../lib/api'
import NewsTicker from '../components/NewsTicker'
import LiveQuote  from '../components/LiveQuote'
import { TIER_CONFIGS } from '../lib/shared'

export default function Dashboard() {
  const { user } = useAuthStore()
  const { data: history } = useQuery({ queryKey: ['chart-history'], queryFn: () => api.charts.history(5) })
  // Assistant "Today" data — what needs attention right now.
  const { data: guardian } = useQuery({ queryKey: ['guarded-positions'], queryFn: () => api.positions.list(), retry: false, refetchInterval: 5 * 60_000 })
  const { data: folio } = useQuery({ queryKey: ['auto-calls-portfolio'], queryFn: () => api.scanner.portfolio(), retry: false, refetchInterval: 5 * 60_000 })
  // Market regime — risk-on/off backdrop (200-day SMA breadth). Slow-moving; refetch every 30 min.
  const { data: regime } = useQuery({ queryKey: ['market-regime'], queryFn: () => api.market.regime(), retry: false, staleTime: 30 * 60_000, refetchInterval: 30 * 60_000 })
  const triggered = (guardian?.positions ?? []).filter((p: any) => p.triggered)
  const money = (n: number | null | undefined) => n == null ? '—' : `${n >= 0 ? '+' : '−'}$${Math.abs(n).toFixed(2)}`
  const clr = (n: number | null | undefined) => n == null ? 'var(--muted)' : n >= 0 ? 'var(--cash)' : 'var(--red)'
  const regimeClr = regime?.verdict === 'RISK-ON' ? 'var(--cash)' : regime?.verdict === 'RISK-OFF' ? 'var(--red)' : 'var(--amber)'

  const tier    = user?.tier ?? 'free'
  const config  = TIER_CONFIGS[tier]
  const pct     = config.monthlyLimit > 0 ? Math.round(((user?.analysesThisMonth ?? 0) / config.monthlyLimit) * 100) : null

  // AI usage — estimated until per-analysis token capture lands. Chart analyses are multimodal
  // (chart images + prompt in, ~2k out): ballpark ~6k tokens and ~$0.08 blended per analysis.
  const analyses  = user?.analysesThisMonth ?? 0
  const estTokens = analyses * 6000
  const estCost   = analyses * 0.08

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <h1 style={{ fontSize: '1.5rem', color: 'var(--bill)', letterSpacing: '-0.02em', marginBottom: 4 }}>
          Welcome back{user?.name ? `, ${user.name}` : ''}
        </h1>
        <div className="eyebrow">Your trading assistant · {new Date().toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}</div>
      </div>

      {/* Market regime — the risk-on/off backdrop; context for every calls decision */}
      {regime?.verdict && regime.verdict !== 'UNKNOWN' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: 'var(--card)', border: `1px solid ${regimeClr}`, borderRadius: 4, padding: '10px 14px' }}>
          <span style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.72rem', color: regimeClr, letterSpacing: '.04em', whiteSpace: 'nowrap' }}>◆ REGIME · {regime.verdict}</span>
          <span style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', flex: 1, minWidth: 200, lineHeight: 1.5 }}>{regime.read}</span>
          <span style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
            {(regime.legs ?? []).map((l: any) => (
              <span key={l.symbol} style={{ marginLeft: 8 }}>{l.symbol} <span style={{ color: l.above ? 'var(--cash)' : 'var(--red)' }}>{l.above ? '↑' : '↓'}{l.distPct >= 0 ? '+' : ''}{l.distPct}%</span></span>
            ))}
          </span>
        </div>
      )}

      {/* Today — what needs attention */}
      {triggered.length > 0 ? (
        <div style={{ background: 'rgba(212,168,67,.08)', border: '1px solid var(--amber)', borderRadius: 4, padding: '14px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
            <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.9rem', color: 'var(--amber)' }}>⚠ {triggered.length} position{triggered.length > 1 ? 's need' : ' needs'} attention</div>
            <Link to="/positions" style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--cash)', textDecoration: 'none' }}>Positions →</Link>
          </div>
          {triggered.map((p: any) => (
            <div key={p.id} style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--text)', padding: '3px 0' }}>
              {p.ticker} ${p.strike}{p.optionType === 'put' ? 'P' : 'C'} — <b>{p.triggered}</b> · <span style={{ color: clr(p.pnl) }}>{money(p.pnl)}</span>
            </div>
          ))}
        </div>
      ) : (guardian?.positions?.length ?? 0) > 0 ? (
        <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontFamily: 'Syne Mono', fontSize: '.66rem', color: 'var(--muted)' }}>✓ All {guardian.positions.length} watched position{guardian.positions.length > 1 ? 's' : ''} within plan · <span style={{ color: clr(guardian.totalPnl) }}>{money(guardian.totalPnl)}</span></div>
          <Link to="/briefing" style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--cash)', textDecoration: 'none' }}>Morning briefing →</Link>
        </div>
      ) : null}

      {/* Stat cards */}
      <div className="stats-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        {/* Analyses this month */}
        <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '16px 18px' }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Analyses This Month</div>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.4rem', color: 'var(--bill)', marginBottom: 2 }}>{tier === 'free' ? '—' : `${analyses} / ${config.monthlyLimit === 999 ? '∞' : config.monthlyLimit}`}</div>
          <div style={{ fontFamily: 'Syne Mono', fontSize: '0.58rem', color: 'var(--muted)' }}>{tier === 'free' ? 'Upgrade to analyze' : pct !== null ? `${pct}% used` : 'Unlimited'}</div>
        </div>

        {/* AI usage — tokens + estimated cost */}
        <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '16px 18px' }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>AI Usage · est.</div>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.4rem', color: 'var(--bill)', marginBottom: 2 }}>~${estCost.toFixed(2)}</div>
          <div style={{ fontFamily: 'Syne Mono', fontSize: '0.58rem', color: 'var(--muted)' }}>≈ {estTokens >= 1000 ? `${(estTokens / 1000).toFixed(1)}k` : estTokens} tokens this month</div>
        </div>

        {/* Auto Calls paper P&L */}
        {folio?.stats && (
          <Link to="/scanner" style={{ textDecoration: 'none', background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '16px 18px', display: 'block' }}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>Auto Calls · paper</div>
            <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.4rem', color: clr(folio.stats.realized + folio.stats.unrealized), marginBottom: 2 }}>{money(folio.stats.realized + folio.stats.unrealized)}</div>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '0.58rem', color: 'var(--muted)' }}>{folio.stats.openCount} open · {folio.stats.closedCount} closed{folio.stats.winRate != null ? ` · ${folio.stats.winRate}% win` : ''}</div>
          </Link>
        )}
      </div>

      {/* Quick actions */}
      <div style={{ display: 'flex', gap: 10 }}>
        <Link to="/analyze" style={{ padding: '10px 20px', background: 'var(--cash)', color: 'var(--ink)', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '0.78rem', textDecoration: 'none', letterSpacing: '.05em' }}>
          + New Analysis
        </Link>
        <Link to="/positions" style={{ padding: '10px 20px', border: '1px solid var(--edge)', color: 'var(--text)', borderRadius: 2, fontFamily: 'Syne', fontWeight: 600, fontSize: '0.78rem', textDecoration: 'none' }}>
          Positions
        </Link>
        <Link to="/scanner" style={{ padding: '10px 20px', border: '1px solid var(--edge)', color: 'var(--text)', borderRadius: 2, fontFamily: 'Syne', fontWeight: 600, fontSize: '0.78rem', textDecoration: 'none' }}>
          Scanner
        </Link>
      </div>

      {/* Recent analyses */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="eyebrow">Recent Analyses</div>
          <Link to="/analyze" style={{ fontFamily: 'Syne Mono', fontSize: '0.6rem', color: 'var(--cash)', textDecoration: 'none' }}>New →</Link>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {history?.length ? history.map((h: any) => (
            <div key={h.id} style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 3, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.5rem', color: h.grade?.[0] === 'A' ? 'var(--cash)' : h.grade?.[0] === 'B' ? '#a8d8a0' : h.grade?.[0] === 'C' ? 'var(--amber)' : 'var(--red)', width: 36 }}>{h.grade}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '0.85rem', marginBottom: 2 }}>{h.ticker} · {h.timeframes}</div>
                <div style={{ fontFamily: 'Syne Mono', fontSize: '0.6rem', color: 'var(--muted)' }}>{h.direction} · {h.confidence}% confidence</div>
              </div>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '0.58rem', color: 'var(--muted)', textAlign: 'right' }}>
                {new Date(h.createdAt * 1000).toLocaleDateString()}
              </div>
            </div>
          )) : (
            <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 3, padding: '28px', textAlign: 'center', fontFamily: 'Syne Mono', fontSize: '0.68rem', color: 'var(--muted)' }}>
              No analyses yet — <Link to="/analyze" style={{ color: 'var(--cash)', textDecoration: 'none' }}>run your first one</Link>
            </div>
          )}
        </div>
      </div>

      {/* Live market data — Super only */}
      {tier === 'super' && <LiveQuote />}

      {/* Market News */}
      <div>
        <NewsTicker />
      </div>

    </div>
  )
}
